import type { PlannerRegion, SavedPlannerRoute } from '../../types';
import type { PlannerOpportunity, PlannerLensInput } from './plannerRecommendationEngine';
import type { RecommendationAccessType, RecommendationBudgetBand } from '../recommendations/catalog';
import { DEFAULT_PLANNER_REGION, getCurrentPlannerMonth } from './seasonality';

const VALID_REGIONS = new Set<PlannerRegion>(['tyrrhenian', 'adriatic', 'ionian', 'sicily_channel', 'western_med', 'aegean_cyprus']);
const VALID_ACCESS_TYPES = new Set<RecommendationAccessType>(['shore', 'boat', 'both']);
const VALID_BUDGET_BANDS = new Set<RecommendationBudgetBand>(['budget', 'mid', 'premium']);
const ROUTE_SETUP_URL_SEPARATOR = '~';

const clampMonthIndex = (value: string | null): number => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 && parsed <= 11 ? parsed : getCurrentPlannerMonth();
};

const clampRegion = (value: string | null): PlannerRegion => {
  return value && VALID_REGIONS.has(value as PlannerRegion) ? (value as PlannerRegion) : DEFAULT_PLANNER_REGION;
};

const clampAccessType = (value: string | null): RecommendationAccessType => {
  return value && VALID_ACCESS_TYPES.has(value as RecommendationAccessType) ? (value as RecommendationAccessType) : 'shore';
};

const clampBudgetBand = (value: string | null): RecommendationBudgetBand => {
  return value && VALID_BUDGET_BANDS.has(value as RecommendationBudgetBand) ? (value as RecommendationBudgetBand) : 'budget';
};

export const buildPlannerRouteId = (
  speciesId: string,
  techniqueId: string,
  rigId?: string | null,
  locationTypeId?: string | null
): string => `${speciesId}:${techniqueId}:${rigId || 'none'}:${locationTypeId || 'none'}`;

export const encodeRouteSetupPathParam = (routeId: string): string =>
  routeId.split(':').map((part) => encodeURIComponent(part)).join(ROUTE_SETUP_URL_SEPARATOR);

export const decodeRouteSetupPathParam = (routeParam: string): string => {
  const decoded = decodeURIComponent(routeParam);
  return decoded.includes(':')
    ? decoded
    : decoded.split(ROUTE_SETUP_URL_SEPARATOR).map((part) => decodeURIComponent(part)).join(':');
};

export const buildRouteSetupPathFromId = (routeId: string, lens: PlannerLensInput): string => {
  const params = new URLSearchParams({
    month: String(lens.monthIndex),
    region: lens.region,
    access: lens.accessType,
    budget: lens.budgetBand
  });

  return `/now/${encodeRouteSetupPathParam(routeId)}?${params.toString()}`;
};

export const buildRouteSetupPath = (opportunity: PlannerOpportunity, lens: PlannerLensInput): string =>
  buildRouteSetupPathFromId(opportunity.id, lens);

export const parseRouteSetupLens = (searchParams: URLSearchParams): PlannerLensInput => ({
  monthIndex: clampMonthIndex(searchParams.get('month')),
  region: clampRegion(searchParams.get('region')),
  accessType: clampAccessType(searchParams.get('access')),
  budgetBand: clampBudgetBand(searchParams.get('budget'))
});

export const createSavedRouteFromOpportunity = (
  opportunity: PlannerOpportunity,
  lens: PlannerLensInput
): SavedPlannerRoute => ({
  routeId: opportunity.id,
  label: `${opportunity.species.name} • ${opportunity.technique.name}`,
  speciesId: opportunity.species.speciesId,
  techniqueId: opportunity.technique.techniqueId,
  rigId: opportunity.rig?.rigId || null,
  locationTypeId: opportunity.location?.locationTypeId || null,
  monthIndex: lens.monthIndex,
  region: lens.region,
  accessType: lens.accessType,
  budgetBand: lens.budgetBand,
  trustLabel: opportunity.trustLabel,
  savedAt: new Date().toISOString()
});
